import { useEffect, useState } from "react";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, TextField } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Button } from "./Button";
import { CountrySelect } from "./CountrySelect";
import { FieldWrapper } from "./FieldWrapper";

export interface EditPhoneValues {
  code_phone_number: string;
  phone_number: string;
}

interface Props {
  open: boolean;
  /** Valores iniciales; se re-sincronizan cada vez que el modal se abre. */
  initialValues: EditPhoneValues;
  /**
   * Se ejecuta al presionar "Editar". Vive en el padre: ahí se hace el patch
   * al API y la actualización del store. Si regresa una promesa, el modal
   * deshabilita sus controles mientras se resuelve.
   */
  onSubmit: (values: EditPhoneValues) => void | Promise<void>;
  onClose: () => void;
  /** Deshabilita los controles por estado del padre (ej. código ya validado). */
  disabled?: boolean;
  title?: string;
  fieldLabel?: string;
  /** Texto auxiliar debajo del campo, como el `hint` de los demás formularios. */
  hint?: string;
  submitLabel?: string;
  cancelLabel?: string;
}

/**
 * Modal para editar el teléfono de un paso del onboarding. Es dueño del estado
 * local del formulario (código de país + número) y no conoce el API ni el store:
 * solo entrega los valores capturados vía `onSubmit`.
 */
export const EditPhoneDialog = ({
  open,
  initialValues,
  onSubmit,
  onClose,
  disabled = false,
  title = "Editar número de teléfono",
  fieldLabel = "Teléfono celular",
  hint,
  submitLabel = "Editar",
  cancelLabel = "Cancelar",
}: Props) => {
  const { code_phone_number, phone_number } = initialValues;
  const [values, setValues] = useState<EditPhoneValues>(initialValues);
  const [submitting, setSubmitting] = useState(false);

  // Se leen los campos por separado para no re-sincronizar en cada render:
  // `initialValues` suele llegar como objeto literal del padre.
  useEffect(() => {
    if (open) setValues({ code_phone_number, phone_number });
  }, [open, code_phone_number, phone_number]);

  const isBlocked = disabled || submitting;

  const handleSubmit = async () => {
    if (isBlocked || !values.phone_number) return;

    try {
      setSubmitting(true);
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ textAlign: "center", color: "primary.dark", position: "relative", pr: 6, pt: 3 }}>
        {title}
        <IconButton
          aria-label="Cerrar"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "primary.dark" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 3 }}>
        <Box sx={{ mt: 1, mb: 3 }}>
          <FieldWrapper label={fieldLabel} hint={hint} full>
            <Box sx={{ display: "flex", gap: 1 }}>
              <CountrySelect
                value={values.code_phone_number}
                onChange={(code) => setValues((prev) => ({ ...prev, code_phone_number: code }))}
                disabled={isBlocked}
              />
              <TextField
                size="lendia_input"
                id="phone_number"
                name="phone_number"
                fullWidth
                autoComplete="off"
                placeholder="55 1234 5678"
                onChange={(ev) => setValues((prev) => ({ ...prev, phone_number: ev.target.value }))}
                value={values.phone_number}
                inputProps={{ maxLength: 10 }}
                disabled={isBlocked}
              />
            </Box>
          </FieldWrapper>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
          <Button tone="ghost" fullWidth disabled={isBlocked} onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button fullWidth disabled={isBlocked || !values.phone_number} onClick={handleSubmit}>
            {submitLabel}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
